import { CommonModule, LOCATION_INITIALIZED } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_INITIALIZER, Injector, LOCALE_ID, NgModule } from '@angular/core';
import {EffectsModule} from '@ngrx/effects';
import {StoreModule} from '@ngrx/store';
import {TranslateService} from '@ngx-translate/core';
import {UserService} from './access-control-mgt/facade/user.service';
import {AccessControlService} from '@carm/_core/access-control-mgt/services';
import {AccessUserService} from '@carm/_core/access-control-mgt/services';
import {UserEffects} from './access-control-mgt/store/user.effects';
import * as fromUser from './access-control-mgt/store/user.reducer';
import {AlertModule} from './alerts/alert.module';
import {ClientGroupService} from '@carm/_core/client-group/services/client-group.service';
import {EmployeesService} from './employees/services/employees.service';
import {AuthRouteGuard} from './guards/auth-route.guard';
import {AuthGuard} from './guards/auth.guard';
import {HttpCustomerHeaderInterceptor} from './interceptors/http-custome header-interceptor.service';
import {LoaderSpinnerInterceptor} from './interceptors/loader-spinner.interceptor.service';
import {LoaderSpinnerService} from './interceptors/loader-spinner.service';
import {MetadataLoadingService, MetadataService} from './metadata/services';
import {NotificationService} from './notification-mgt/facade/notifcation.service';
import {ProgramAccountProfileService} from './program-account-profile/services/program-account-profile.service';
import {RolePermissionLoadingService, RoleTypePermissionService} from './role-type-permissions/services';
import {RulingsService} from './rulings/services';
import { CaseService } from './case/services/case.service';
import { DocumentService } from './attachments/services/document.service';
import { DocumentsCollectionLettersService } from './attachments/services/documents-collection-letters.service';
import { DocumentsRegistrationEnrolmentService } from './attachments/services/documents-registration-enrolment.service';
import { DocumentsDDBClaimantService } from './attachments/services/documents-ddb-claimant.service';
import { CraService } from './cra/services/cra.service';
import { BusinessService } from './business/services/business.service';
import { StaffAccessRequestsService } from './staff-access-requests/services/staff-access-requests.service';
import { GrantAccessRequestsStateService } from '@carm/features/private/setup-my-portal/employees-access-mgt/services/grant-access-requests-state.service';
import { APIKeyService } from '@carm/features/private/setup-my-portal/view-api-key/services';  //to do fix: should not directly import feature service
import { TransactionHistoryService } from './financial/services/transaction-history.service';
import { BrokerSummaryService } from './financial/services/broker-summary.service';
import { FinancialReportService } from './financial/services/financial-report.service';
import { FinancialSecurityEnrolmentStateService } from '@carm/features/private/financial/features/financial-security/services/financial-security-enrolment-state.service';
import { FinancialSecurityService } from './financial-security/services/financial-security.service';
import { RegisterBusinessService } from './business-registration/services/register-business.service';
import { LoggerService } from './logger/logger.service';
import { AppealsService } from './appeals/services/appeals.service';
import { SuretyProviderService } from './financial-security/services/surety-provider.service';
import { DashboardCountService } from './dashboard-count/services/dashboard-count.service';
import { CarmRouter } from './carm-router/services/carm-router.service';
import { MonerisService } from './moneris/services/moneris.service';
import { PaymentsService } from './payments/services/payments.service';
import { PaymentArrangementService } from './payments/services/payment-arrangement.service';
import { EnforcementService } from './enforcement/services/enforcement.service';
import { PreAuthorizeDebitService } from './payments/services/pre-authorize-debit.service';
import { FinancialSecurityNavigationService } from '@carm/features/private/financial/features/financial-security/services/financial-security-navigation.service';
import { FinancialSecurityBondStateService } from '@carm/features/private/financial/features/financial-security/services/financial-security-bond-state.service';
import { FinancialSecurityEnrolmentService } from './financial-security/services/financial-security-enrolment.service';
import { DeclarationsCoreModule } from './declarations/declarations-core.module';
import { LanguageService } from '@carm/_auth/spartacus/core/site-context';
import { DeploymentService } from './deployment/services/deployment.service';
import { ProgramFacadeService } from './program-account-profile/services/program-facade.service';

@NgModule({
  imports: [
    CommonModule,
    StoreModule.forFeature(fromUser.userFeatureKey, fromUser.reducer),
    EffectsModule.forFeature([UserEffects]),
    AlertModule,
    DeclarationsCoreModule
  ],
  providers: [
    AuthGuard,
    AuthRouteGuard,
    AccessControlService,
    LoaderSpinnerService,
    RoleTypePermissionService,
    MetadataService,
    MetadataLoadingService,
    NotificationService,
    AccessUserService,
    RolePermissionLoadingService,
    UserService,
    TransactionHistoryService,
    BrokerSummaryService,
    ProgramAccountProfileService,
    ProgramFacadeService,
    StaffAccessRequestsService,
    GrantAccessRequestsStateService,
    RulingsService,
    EmployeesService,
    ClientGroupService,
    CaseService,
    DocumentService,
    DocumentsCollectionLettersService,
    DocumentsRegistrationEnrolmentService,
    DocumentsDDBClaimantService,
    CraService,
    FinancialSecurityService,
    SuretyProviderService,
    FinancialReportService,
    LanguageService,
    APIKeyService,
    BusinessService,
    FinancialSecurityEnrolmentStateService,
    RegisterBusinessService,
    LoggerService,
    AppealsService,
    DashboardCountService,
    MonerisService,
    PaymentsService,
    PaymentArrangementService,
    EnforcementService,
    PreAuthorizeDebitService,
    FinancialSecurityNavigationService,
    FinancialSecurityBondStateService,
    FinancialSecurityEnrolmentService,
    DeploymentService,
    CarmRouter,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderSpinnerInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpCustomerHeaderInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER, useFactory: setLocale,
      deps: [LOCALE_ID, LanguageService, MetadataService, TranslateService, Injector],
      multi: true,
    },
  ],
})
export class CoreModule {
}

export function setLocale(localeId: string, languageService: LanguageService, metadataService: MetadataService, translate: TranslateService, injector: Injector) {
  return () => new Promise<any>((resolve: any) => {
    const locationInitialized = injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
    locationInitialized.then(() => {
      const currentLocal = (localeId === 'en' || localeId === 'fr') ? localeId : 'en';
      metadataService.setCutoverDates();
      translate.setDefaultLang(currentLocal);
      languageService.setActive(currentLocal); // set store
      translate.use(currentLocal).subscribe(() => {}, err => {}, () => {resolve(null);});
    });
  });
}
